// components/subtitle/SubtitleSearchModal.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useVideo } from "../../context/VideoContext";
import { useSelectedSubtitles } from "../../context/SelectedSubtitlesContext";
import {
  searchSubtitles,
  fetchSubtitleContent,
} from "../../services/subtitleSearchApi";
import useVideoManager from "../../hooks/useVideoManager";
import LoadingModal from "../VideoPlayer/LoadingModal";
import srtParser2 from "srt-parser-2";
import "./SubtitleSearchModal.css";
import { getLanguageColor, getLanguageName, getMainLanguageOptions, getAllLanguageOptions } from "../../utils/languages";

const SubtitleSearchModal = ({ onClose, onSuccess }) => {
  const { videoMetadata, isPlaying, pauseVideo } = useVideo();
  const { subtitleHistory, addToHistory } = useSelectedSubtitles();
  const { loadLocalSubtitle } = useVideoManager();

  // Refs
  const searchResultsRef = useRef(null);
  const formRef = useRef(null); // رفرنس برای فرم جستجو

  // State
  const [searchForm, setSearchForm] = useState({
    name: videoMetadata.filmName || "",
    type: videoMetadata.videoType || "movie",
    language: "all",
    season: videoMetadata.season || "",
    episode: videoMetadata.episode || "",
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedForDownload, setSelectedForDownload] = useState({
    1: null,
    2: null,
  });
  const [downloading, setDownloading] = useState(false);

  // توقف ویدیو هنگام باز شدن مودال
  useEffect(() => {
    if (isPlaying) {
      pauseVideo();
    }
  }, [isPlaying, pauseVideo]);

  // آپدیت فرم با متادیتای ویدیو
  useEffect(() => {
    if (videoMetadata?.filmName) {
      setSearchForm({
        name: videoMetadata.filmName || "",
        type: videoMetadata.videoType || "movie",
        language: "all",
        season: videoMetadata.season || "",
        episode: videoMetadata.episode || "",
      });

      if (videoMetadata.filmName) {
        setTimeout(() => {
          handleSearch(1);
        }, 100);
      }
    }
  }, [videoMetadata]);

  // آپدیت فرم بر اساس نوع
  useEffect(() => {
    if (searchForm.type === "movie") {
      setSearchForm((prev) => ({ ...prev, season: "", episode: "" }));
    }
  }, [searchForm.type]);

  // اسکرول به بالای نتایج
  const scrollToTop = useCallback(() => {
    if (searchResultsRef.current) {
      searchResultsRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  // تابع جستجو
  const handleSearch = async (page = 1) => {
    if (!searchForm.name.trim()) return;

    setLoading(true);
    try {
      const params = {
        name: searchForm.name,
        type: searchForm.type,
        language: searchForm.language,
      };

      if (searchForm.type === "series") {
        if (searchForm.season) params.season = searchForm.season;
        if (searchForm.episode) params.episode = searchForm.episode;
      }

      const data = await searchSubtitles(params, page);

      if (data.success) {
        setResults(data.data.docs);
        setTotalPages(data.data.totalPages);
        setCurrentPage(data.data.page);

        // اسکرول به بالای نتایج بعد از جستجوی جدید
        if (page === 1) {
          setTimeout(() => scrollToTop(), 100);
        }
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  const isSubtitleAdded = (subtitleId) => {
    return subtitleHistory.some((sub) => sub._id === subtitleId);
  };

  const isSubtitleSelected = (subtitleId, targetNumber) => {
    return selectedForDownload[targetNumber]?._id === subtitleId;
  };

  const handleSelectSubtitle = (subtitle, targetNumber) => {
    if (isSubtitleAdded(subtitle._id)) return;
    setSelectedForDownload((prev) => ({
      ...prev,
      [targetNumber]:
        prev[targetNumber]?._id === subtitle._id ? null : subtitle,
    }));
  };

  const handleDownloadSelected = async () => {
    const subsToDownload = [];
    if (selectedForDownload[1])
      subsToDownload.push({ ...selectedForDownload[1], targetNumber: 1 });
    if (selectedForDownload[2])
      subsToDownload.push({ ...selectedForDownload[2], targetNumber: 2 });

    if (subsToDownload.length === 0) return;

    setDownloading(true);
    try {
      for (const sub of subsToDownload) {
        const content = await fetchSubtitleContent(
          sub.subtitlesUploadId || sub._id,
        );

        const parser = new srtParser2();
        const cues = parser.fromSrt(content);

        const subtitleInfo = {
          sourceType: "online",
          subtitlesUploadId: sub.subtitlesUploadId || sub._id,
          language: sub.subtitlesLanguage,
          nameOfMovie: sub.nameOfMovie,
          season: sub.season,
          episode: sub.episode,
          videoType: sub.videoType || "movie",
          coverUrl: sub.coverUrl,
          otherTags: sub.otherTags,
          productionYear: sub.productionYear,
          cues: cues,
          rawContent: content,
          userId: sub.userId,
          groupId: sub.groupId,
        };

        addToHistory(subtitleInfo, sub.targetNumber);

        const file = new File(
          [content],
          `${sub.nameOfMovie}_${sub.subtitlesLanguage}_${sub.targetNumber}.srt`,
          { type: "text/plain" },
        );

        await loadLocalSubtitle(
          file,
          sub.subtitlesLanguage,
          sub.targetNumber,
          subtitleInfo,
        );
      }

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error downloading subtitles:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="ssm-overlay">
        <div className="ssm-modal">
          {/* هدر */}
          <div className="ssm-header">
            <h2>جستجوی زیرنویس</h2>
            <button className="ssm-close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="ssm-search-section">
            {/* نتایج */}
            <div className="ssm-search-results" ref={searchResultsRef}>
              {/* فرم جستجو - الان داخل بخش اسکرول شونده است */}
              <div className="ssm-search-form">
                <div className="ssm-form-row">
                  <div className="ssm-form-group">
                    <input
                      type="text"
                      value={searchForm.name}
                      onChange={(e) =>
                        setSearchForm({ ...searchForm, name: e.target.value })
                      }
                      placeholder="نام فیلم یا سریال"
                    />
                  </div>
                  <div className="ssm-form-group">
                    <select
                      value={searchForm.language}
                      onChange={(e) =>
                        setSearchForm({
                          ...searchForm,
                          language: e.target.value,
                        })
                      }
                    >
                      <option value="all">همه زبان‌ها</option>
                      {getMainLanguageOptions().map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="ssm-search-btn"
                    onClick={() => handleSearch(1)}
                    disabled={!searchForm.name.trim() || loading}
                  >
                    {loading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-search"></i>
                    )}
                  </button>
                </div>

                <div className="ssm-form-row">
                  <div className="ssm-form-group">
                    <select
                      value={searchForm.type}
                      onChange={(e) =>
                        setSearchForm({ ...searchForm, type: e.target.value })
                      }
                    >
                      <option value="movie">فیلم</option>
                      <option value="series">سریال</option>
                    </select>
                  </div>

                  {searchForm.type === "series" ? (
                    <>
                      <div className="ssm-form-group">
                        <input
                          type="number"
                          value={searchForm.season}
                          onChange={(e) =>
                            setSearchForm({
                              ...searchForm,
                              season: e.target.value,
                            })
                          }
                          placeholder="فصل"
                        />
                      </div>
                      <div className="ssm-form-group">
                        <input
                          type="number"
                          value={searchForm.episode}
                          onChange={(e) =>
                            setSearchForm({
                              ...searchForm,
                              episode: e.target.value,
                            })
                          }
                          placeholder="قسمت"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="ssm-form-group ssm-hidden-input">
                      <input placeholder="" disabled />
                    </div>
                  )}
                </div>
              </div>

              {results.length > 0 ? (
                <>
                  <div className="ssm-results-list">
                    {results.map((subtitle) => {
                      const langColor = getLanguageColor(
                        subtitle.subtitlesLanguage,
                      );
                      const added = isSubtitleAdded(subtitle._id);

                      return (
                        <div
                          key={subtitle._id}
                          className={`ssm-subtitle-item ${
                            added ? "ssm-added" : ""
                          }`}
                        >
                          <div className="ssm-subtitle-info">
                            <div className="ssm-main-info">
                              <span
                                className="ssm-language-badge"
                                style={{
                                  backgroundColor: langColor.bg,
                                  color: langColor.text,
                                }}
                              >
                                {getLanguageName(subtitle.subtitlesLanguage)}
                              </span>

                              <span className="ssm-name">
                                {subtitle.nameOfMovie}
                                {subtitle.season && subtitle.episode && (
                                  <span className="ssm-episode-info">
                                    S{subtitle.season}E{subtitle.episode}
                                  </span>
                                )}
                                <span
                                  className="ssm-subtitle-id"
                                  title="شناسه زیرنویس"
                                >
                                  {subtitle._id.slice(-6)}
                                </span>
                              </span>

                              {added && (
                                <span
                                  className="ssm-added-badge"
                                  title="قبلاً اضافه شده"
                                >
                                  ✓
                                </span>
                              )}
                            </div>

                            {subtitle.otherTags?.length > 0 && (
                              <div className="ssm-tags">
                                {subtitle.otherTags.map((tag) => (
                                  <span key={tag} className="ssm-tag">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="ssm-selection-buttons">
                            <button
                              className={`ssm-selection-btn ${
                                isSubtitleSelected(subtitle._id, 1)
                                  ? "ssm-selected"
                                  : ""
                              }`}
                              onClick={() => handleSelectSubtitle(subtitle, 1)}
                              disabled={
                                added ||
                                selectedForDownload[2]?._id === subtitle._id
                              }
                            >
                              {isSubtitleSelected(subtitle._id, 1) ? "✓" : "۱"}
                            </button>

                            <button
                              className={`ssm-selection-btn ${
                                isSubtitleSelected(subtitle._id, 2)
                                  ? "ssm-selected"
                                  : ""
                              }`}
                              onClick={() => handleSelectSubtitle(subtitle, 2)}
                              disabled={
                                added ||
                                selectedForDownload[1]?._id === subtitle._id
                              }
                            >
                              {isSubtitleSelected(subtitle._id, 2) ? "✓" : "۲"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="ssm-pagination">
                      <button
                        disabled={currentPage === 1}
                        onClick={() => handleSearch(currentPage - 1)}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>

                      <span>
                        صفحه {currentPage} از {totalPages}
                      </span>

                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => handleSearch(currentPage + 1)}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                !loading && (
                  <div className="ssm-no-results">
                    <i className="fas fa-search"></i>
                    <p>برای جستجو عبارت مورد نظر را وارد کنید</p>
                  </div>
                )
              )}
            </div>

            {/* فوتر */}
            {(selectedForDownload[1] || selectedForDownload[2]) && (
              <div className="ssm-footer">
                <div className="ssm-selected-info">
                  {selectedForDownload[1] && (
                    <span className="ssm-selected-badge">
                      زیرنویس ۱:{" "}
                      {getLanguageName(selectedForDownload[1].subtitlesLanguage)}
                    </span>
                  )}
                  {selectedForDownload[2] && (
                    <span className="ssm-selected-badge">
                      زیرنویس ۲:{" "}
                      {getLanguageName(selectedForDownload[2].subtitlesLanguage)}
                    </span>
                  )}
                </div>

                <button
                  className="ssm-download-btn"
                  onClick={handleDownloadSelected}
                  disabled={downloading}
                >
                  {downloading ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fas fa-download"></i>
                  )}
                  {downloading ? "در حال دریافت..." : "افزودن زیرنویس‌ها"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {downloading && <LoadingModal message="در حال دریافت زیرنویس..." />}
    </>
  );
};

export default SubtitleSearchModal;
